<template>
  <div class="owner-management">
    <div class="page-header">
      <h2>店主管理</h2>
      <el-button type="primary" @click="showCreateDialog">新增店主</el-button>
    </div>

    <!-- 搜索表单 -->
    <el-form :inline="true" :model="searchForm" class="search-form">
      <el-form-item label="关键词">
        <el-input v-model="searchForm.keyword" placeholder="请输入姓名、手机号或用户名" clearable />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="searchForm.status" placeholder="请选择状态" clearable>
          <el-option label="待审核" :value="0" />
          <el-option label="正常" :value="1" />
          <el-option label="禁用" :value="2" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleSearch">搜索</el-button>
        <el-button @click="handleReset">重置</el-button>
      </el-form-item>
    </el-form>

    <!-- 数据表格 -->
    <el-table v-loading="loading" :data="ownerList" border style="width: 100%">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="username" label="用户名" />
      <el-table-column prop="real_name" label="真实姓名" />
      <el-table-column prop="mobile" label="手机号码" />
      <el-table-column prop="email" label="邮箱地址" />
      <el-table-column prop="gender" label="性别" width="80">
        <template slot-scope="scope">
          {{ scope.row.gender === 1 ? '男' : scope.row.gender === 2 ? '女' : '未知' }}
        </template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="100">
        <template slot-scope="scope">
          <el-tag :type="scope.row.status === 1 ? 'success' : scope.row.status === 2 ? 'danger' : 'warning'">
            {{ scope.row.status === 1 ? '正常' : scope.row.status === 2 ? '禁用' : '待审核' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="shop_count" label="店铺数量" width="100">
        <template slot-scope="scope">
          <el-link type="primary" @click="viewShops(scope.row.id)">
            {{ scope.row.shop_count || 0 }} 个
          </el-link>
        </template>
      </el-table-column>
      <el-table-column prop="login_count" label="登录次数" width="100" />
      <el-table-column prop="last_login_time" label="最后登录" width="180" />
      <el-table-column prop="create_time" label="创建时间" width="180" />
      <el-table-column label="操作" width="300" fixed="right">
        <template slot-scope="scope">
          <div class="table-operations">
            <el-button size="mini" type="text" @click="handleView(scope.row)">查看</el-button>
            <el-button size="mini" type="text" class="edit-btn" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button size="mini" type="text" class="password-btn" @click="handleChangePassword(scope.row)">改密</el-button>
            <el-button
              size="mini"
              type="text"
              :class="scope.row.status === 1 ? 'disable-btn' : 'enable-btn'"
              @click="handleChangeStatus(scope.row)"
            >
              {{ scope.row.status === 1 ? '禁用' : '启用' }}
            </el-button>
            <el-button size="mini" type="text" class="delete-btn" @click="handleDelete(scope.row)">删除</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        :current-page="pagination.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.limit"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 创建/编辑对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="900px">
      <el-form ref="ownerForm" :model="ownerForm" :rules="ownerRules" label-width="120px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="用户名" prop="username">
              <el-input v-model="ownerForm.username" placeholder="请输入用户名" :disabled="isEdit" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="真实姓名" prop="real_name">
              <el-input v-model="ownerForm.real_name" placeholder="请输入真实姓名" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="手机号码" prop="mobile">
              <el-input v-model="ownerForm.mobile" placeholder="请输入手机号码" :disabled="isEdit" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="邮箱地址" prop="email">
              <el-input v-model="ownerForm.email" placeholder="请输入邮箱地址" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item :label="isEdit ? '登录密码（不填不修改）' : '登录密码'" prop="password">
              <el-input v-model="ownerForm.password" type="password" :placeholder="isEdit ? '不填则不修改密码' : '请输入登录密码'" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="性别" prop="gender">
              <el-select v-model="ownerForm.gender" placeholder="请选择性别">
                <el-option label="未知" :value="0" />
                <el-option label="男" :value="1" />
                <el-option label="女" :value="2" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="证件类型" prop="license_type">
              <el-select v-model="ownerForm.license_type" placeholder="请选择证件类型">
                <el-option label="个人" :value="1" />
                <el-option label="企业" :value="2" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="证件号码" prop="license_number">
              <el-input v-model="ownerForm.license_number" placeholder="请输入证件号码" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="身份证号" prop="id_card">
              <el-input v-model="ownerForm.id_card" placeholder="请输入身份证号" :disabled="isEdit" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="联系电话" prop="contact_phone">
              <el-input v-model="ownerForm.contact_phone" placeholder="请输入联系电话" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="联系地址" prop="address">
          <el-input v-model="ownerForm.address" placeholder="请输入联系地址" />
        </el-form-item>
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="省份" prop="province">
              <el-input v-model="ownerForm.province" placeholder="请输入省份" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="城市" prop="city">
              <el-input v-model="ownerForm.city" placeholder="请输入城市" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="区县" prop="district">
              <el-input v-model="ownerForm.district" placeholder="请输入区县" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="头像" prop="avatar">
          <div class="avatar-upload-container">
            <el-upload
              class="avatar-uploader"
              :action="uploadAction"
              :show-file-list="false"
              :before-upload="beforeAvatarUpload"
              :on-success="handleAvatarSuccess"
              :on-error="handleUploadError"
              :headers="uploadHeaders"
              :data="uploadData"
            >
              <img v-if="ownerForm.avatar" :src="ownerForm.avatar" class="avatar-preview">
              <i v-else class="el-icon-plus avatar-uploader-icon"></i>
            </el-upload>
            <div class="avatar-upload-tips">
              <p>支持 JPG、PNG、GIF、WEBP 格式</p>
              <p>图片大小不超过 5MB</p>
              <p>建议尺寸：200x200 像素</p>
            </div>
          </div>
        </el-form-item>
        <el-form-item label="微信二维码" prop="wechat_qrcode">
          <div class="avatar-upload-container">
            <el-upload
              class="avatar-uploader"
              :action="uploadAction"
              :show-file-list="false"
              :before-upload="beforeQrcodeUpload"
              :on-success="handleQrcodeSuccess"
              :on-error="handleUploadError"
              :headers="uploadHeaders"
              :data="{ path: 'admin/qrcodes' }"
            >
              <img v-if="ownerForm.wechat_qrcode" :src="ownerForm.wechat_qrcode" class="avatar-preview">
              <i v-else class="el-icon-plus avatar-uploader-icon"></i>
            </el-upload>
            <div class="avatar-upload-tips">
              <p>支持 JPG、PNG、GIF、WEBP 格式</p>
              <p>图片大小不超过 5MB</p>
              <p>建议尺寸：300x300 像素</p>
            </div>
          </div>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-select v-model="ownerForm.status" placeholder="请选择状态">
            <el-option label="待审核" :value="0" />
            <el-option label="正常" :value="1" />
            <el-option label="禁用" :value="2" />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">确定</el-button>
      </div>
    </el-dialog>

    <!-- 修改密码对话框 -->
    <el-dialog title="修改密码" :visible.sync="passwordDialogVisible" width="500px">
      <el-form ref="passwordForm" :model="passwordForm" :rules="passwordRules" label-width="100px">
        <el-form-item label="店主姓名">
          <el-input v-model="passwordForm.real_name" disabled />
        </el-form-item>
        <el-form-item label="新密码" prop="password">
          <el-input v-model="passwordForm.password" type="password" placeholder="请输入新密码" show-password />
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input v-model="passwordForm.confirmPassword" type="password" placeholder="请再次输入新密码" show-password />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="passwordDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmitPassword">确定</el-button>
      </div>
    </el-dialog>

    <!-- 查看详情对话框 -->
    <el-dialog title="店主详情" :visible.sync="detailVisible" width="900px">
      <div v-if="currentOwner" class="owner-detail">
        <!-- 基本信息 -->
        <div class="detail-section">
          <div class="section-title">基本信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">店主ID</div>
              <div class="detail-value">{{ currentOwner.id }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">用户名</div>
              <div class="detail-value">{{ currentOwner.username }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">真实姓名</div>
              <div class="detail-value">{{ currentOwner.real_name }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">性别</div>
              <div class="detail-value">
                {{ currentOwner.gender === 1 ? '男' : currentOwner.gender === 2 ? '女' : '未知' }}
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">生日</div>
              <div class="detail-value">{{ currentOwner.birthday || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">头像</div>
              <div class="detail-value">
                <el-image
                  v-if="currentOwner.avatar"
                  :src="currentOwner.avatar"
                  style="width: 60px; height: 60px; border-radius: 4px; cursor: pointer;"
                  fit="cover"
                  :preview-src-list="[currentOwner.avatar]"
                />
                <span v-else>-</span>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">微信二维码</div>
              <div class="detail-value">
                <el-image
                  v-if="currentOwner.wechat_qrcode"
                  :src="currentOwner.wechat_qrcode"
                  style="width: 60px; height: 60px; border-radius: 4px; cursor: pointer;"
                  fit="cover"
                  :preview-src-list="[currentOwner.wechat_qrcode]"
                />
                <span v-else>-</span>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">状态</div>
              <div class="detail-value">
                <el-tag :type="currentOwner.status === 1 ? 'success' : currentOwner.status === 2 ? 'danger' : 'warning'">
                  {{ currentOwner.status === 1 ? '正常' : currentOwner.status === 2 ? '禁用' : '待审核' }}
                </el-tag>
              </div>
            </div>
          </div>
        </div>

        <!-- 联系信息 -->
        <div class="detail-section">
          <div class="section-title">联系信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">手机号码</div>
              <div class="detail-value">{{ currentOwner.mobile }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">联系电话</div>
              <div class="detail-value">{{ currentOwner.contact_phone || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">邮箱地址</div>
              <div class="detail-value">{{ currentOwner.email || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">联系地址</div>
              <div class="detail-value">{{ currentOwner.address || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">省份</div>
              <div class="detail-value">{{ currentOwner.province || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">城市</div>
              <div class="detail-value">{{ currentOwner.city || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">区县</div>
              <div class="detail-value">{{ currentOwner.district || '-' }}</div>
            </div>
          </div>
        </div>

        <!-- 证件信息 -->
        <div class="detail-section">
          <div class="section-title">证件信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">身份证号</div>
              <div class="detail-value">{{ currentOwner.id_card || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">证件类型</div>
              <div class="detail-value">
                {{ currentOwner.license_type === 1 ? '个人' : currentOwner.license_type === 2 ? '企业' : '-' }}
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">证件号码</div>
              <div class="detail-value">{{ currentOwner.license_number || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">营业执照</div>
              <div class="detail-value">
                <el-image 
                  v-if="currentOwner.business_license" 
                  :src="currentOwner.business_license" 
                  style="width: 200px; height: 120px; border-radius: 4px; cursor: pointer;"
                  fit="cover"
                  :preview-src-list="[currentOwner.business_license]"
                />
                <span v-else>-</span>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">身份证正面</div>
              <div class="detail-value">
                <el-image 
                  v-if="currentOwner.id_card_front" 
                  :src="currentOwner.id_card_front" 
                  style="width: 200px; height: 120px; border-radius: 4px; cursor: pointer;"
                  fit="cover"
                  :preview-src-list="getIdCardPreviewList"
                />
                <span v-else>-</span>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">身份证背面</div>
              <div class="detail-value">
                <el-image 
                  v-if="currentOwner.id_card_back" 
                  :src="currentOwner.id_card_back" 
                  style="width: 200px; height: 120px; border-radius: 4px; cursor: pointer;"
                  fit="cover"
                  :preview-src-list="getIdCardPreviewList"
                />
                <span v-else>-</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 其他信息 -->
        <div class="detail-section">
          <div class="section-title">其他信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">店铺数量</div>
              <div class="detail-value">
                <el-link type="primary" @click="viewShops(currentOwner.id)">
                  {{ currentOwner.shop_count || 0 }} 个
                </el-link>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">登录次数</div>
              <div class="detail-value">{{ currentOwner.login_count || 0 }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">最后登录时间</div>
              <div class="detail-value">{{ currentOwner.last_login_time || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">最后登录IP</div>
              <div class="detail-value">{{ currentOwner.last_login_ip || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">创建时间</div>
              <div class="detail-value">{{ currentOwner.create_time || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">更新时间</div>
              <div class="detail-value">{{ currentOwner.update_time || '-' }}</div>
            </div>
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailVisible = false">关闭</el-button>
        <el-button type="primary" @click="viewShops(currentOwner.id)">查看店铺</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getOwnerList, getOwnerDetail, addOwner, editOwner, deleteOwner, changeOwnerStatus, changeOwnerPassword } from '@/api/store'
import { uploadImage } from '@/api/upload'

export default {
  name: 'OwnerManagement',
  data() {
    // 自定义验证规则
    const validateMobile = (rule, value, callback) => {
      const mobileReg = /^1[3-9]\d{9}$/
      if (!value) {
        callback(new Error('请输入手机号码'))
      } else if (!mobileReg.test(value)) {
        callback(new Error('请输入正确的手机号码'))
      } else {
        callback()
      }
    }

    const validateEmail = (rule, value, callback) => {
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        callback(new Error('请输入正确的邮箱地址'))
      } else {
        callback()
      }
    }

    const validateIdCard = (rule, value, callback) => {
      if (value && !/(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/.test(value)) {
        callback(new Error('请输入正确的身份证号'))
      } else {
        callback()
      }
    }

    return {
      loading: false,
      ownerList: [],
      searchForm: {
        keyword: '',
        status: null
      },
      pagination: {
        page: 1,
        limit: 20,
        total: 0
      },
      dialogVisible: false,
      detailVisible: false,
      passwordDialogVisible: false,
      dialogTitle: '新增店主',
      isEdit: false,
      currentOwner: null,
      passwordForm: {
        id: null,
        real_name: '',
        password: '',
        confirmPassword: ''
      },
      passwordRules: {
        password: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能少于 6 位', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请再次输入密码', trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              if (value !== this.passwordForm.password) {
                callback(new Error('两次输入的密码不一致'))
              } else {
                callback()
              }
            },
            trigger: 'blur'
          }
        ]
      },
      ownerForm: {
        id: null,
        username: '',
        password: '',
        real_name: '',
        mobile: '',
        email: '',
        gender: 0,
        birthday: '',
        id_card: '',
        contact_phone: '',
        address: '',
        province: '',
        city: '',
        district: '',
        license_type: 1,
        license_number: '',
        avatar: '',
        wechat_qrcode: '',
        status: 0
      },
      ownerRules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
          { min: 3, max: 50, message: '用户名长度在 3 到 50 个字符', trigger: 'blur' }
        ],
        password: [
          {
            validator: (rule, value, callback) => {
              if (this.isEdit) {
                // 编辑模式，密码为可选
                if (value && value.length < 6) {
                  callback(new Error('密码长度不能少于 6 位'))
                } else {
                  callback()
                }
              } else {
                // 新增模式，密码必填
                if (!value) {
                  callback(new Error('请输入登录密码'))
                } else if (value.length < 6) {
                  callback(new Error('密码长度不能少于 6 位'))
                } else {
                  callback()
                }
              }
            },
            trigger: 'blur'
          }
        ],
        real_name: [
          { required: true, message: '请输入真实姓名', trigger: 'blur' },
          { max: 50, message: '姓名长度不能超过 50 个字符', trigger: 'blur' }
        ],
        mobile: [
          { required: true, validator: validateMobile, trigger: 'blur' }
        ],
        email: [
          { validator: validateEmail, trigger: 'blur' }
        ],
        id_card: [
          { validator: validateIdCard, trigger: 'blur' }
        ]
      },
      // 上传相关
      uploadAction: '',
      uploadHeaders: {},
      uploadData: {
        path: 'admin/avatars'
      }
    }
  },
  computed: {
    getIdCardPreviewList() {
      if (!this.currentOwner) return []
      const list = []
      if (this.currentOwner.id_card_front) {
        list.push(this.currentOwner.id_card_front)
      }
      if (this.currentOwner.id_card_back) {
        list.push(this.currentOwner.id_card_back)
      }
      return list
    }
  },
  created() {
    this.fetchOwnerList()
    this.initUpload()
  },
  methods: {
    // 初始化上传配置
    initUpload() {
      // 获取 token
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      this.uploadHeaders = {
        'Authorization': `Bearer ${token}`
      }
      // 设置上传地址（这里使用空字符串，因为我们会用自定义上传方法）
      this.uploadAction = ''
    },
    
    // 上传前验证
    beforeAvatarUpload(file) {
      return this.beforeImageUpload(file, 'avatar')
    },

    // 二维码上传前验证
    beforeQrcodeUpload(file) {
      return this.beforeImageUpload(file, 'qrcode')
    },

    // 通用图片上传验证
    beforeImageUpload(file, type) {
      // 验证文件类型
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
      const isImage = allowedTypes.includes(file.type)
      if (!isImage) {
        this.$message.error('只能上传 JPG、PNG、GIF、WEBP 格式的图片！')
        return false
      }

      // 验证文件大小
      const maxSize = 5 * 1024 * 1024 // 5MB
      const isLt5M = file.size <= maxSize
      if (!isLt5M) {
        this.$message.error('图片大小不能超过 5MB！')
        return false
      }

      // 显示上传中提示
      this.uploadLoading = this.$loading({
        lock: true,
        text: '上传中...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })

      // 使用自定义上传方法
      this.handleCustomUpload(file, type)

      return false // 阻止默认上传行为
    },
    
    // 自定义上传方法
    async handleCustomUpload(file, type = 'avatar') {
      try {
        const path = type === 'qrcode' ? 'admin/qrcodes' : 'admin/avatars'
        const response = await uploadImage(file, path)

        if (response.code === 200) {
          // 上传成功，设置对应的图片 URL
          if (type === 'qrcode') {
            this.ownerForm.wechat_qrcode = response.data.url
            this.$message.success('二维码上传成功！')
          } else {
            this.ownerForm.avatar = response.data.url
            this.$message.success('头像上传成功！')
          }
        } else {
          this.$message.error(response.msg || '上传失败')
        }
      } catch (error) {
        console.error('上传失败:', error)
        this.$message.error('上传失败，请重试')
      } finally {
        // 关闭加载提示
        if (this.uploadLoading) {
          this.uploadLoading.close()
        }
      }
    },

    // 头像上传成功回调
    handleAvatarSuccess(response, file) {
      if (response.code === 200) {
        this.ownerForm.avatar = response.data.url
        this.$message.success('头像上传成功！')
      } else {
        this.$message.error(response.msg || '上传失败')
      }
    },

    // 二维码上传成功回调
    handleQrcodeSuccess(response, file) {
      if (response.code === 200) {
        this.ownerForm.wechat_qrcode = response.data.url
        this.$message.success('二维码上传成功！')
      } else {
        this.$message.error(response.msg || '上传失败')
      }
    },
    

    
    // 上传失败回调
    handleUploadError(error) {
      console.error('上传失败:', error)
      this.$message.error('上传失败，请重试')
      if (this.uploadLoading) {
        this.uploadLoading.close()
      }
    },
    
    async fetchOwnerList() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.page,
          limit: this.pagination.limit,
          ...this.searchForm
        }
        const response = await getOwnerList(params)
        this.ownerList = response.data.list
        this.pagination.total = response.data.count
      } catch (error) {
        this.$message.error('获取店主列表失败')
      } finally {
        this.loading = false
      }
    },
    
    handleSearch() {
      this.pagination.page = 1
      this.fetchOwnerList()
    },
    
    handleReset() {
      this.searchForm = {
        keyword: '',
        status: null
      }
      this.pagination.page = 1
      this.fetchOwnerList()
    },
    
    handleSizeChange(val) {
      this.pagination.limit = val
      this.fetchOwnerList()
    },
    
    handleCurrentChange(val) {
      this.pagination.page = val
      this.fetchOwnerList()
    },
    
    showCreateDialog() {
      this.isEdit = false
      this.dialogTitle = '新增店主'
      this.ownerForm = {
        id: null,
        username: '',
        password: '',
        real_name: '',
        mobile: '',
        email: '',
        gender: 0,
        birthday: '',
        id_card: '',
        contact_phone: '',
        address: '',
        province: '',
        city: '',
        district: '',
        license_type: 1,
        license_number: '',
        avatar: '',
        wechat_qrcode: '',
        status: 0
      }
      this.dialogVisible = true
    },
    
    async handleView(row) {
      try {
        const response = await getOwnerDetail({ id: row.id })
        this.currentOwner = response.data
        this.detailVisible = true
      } catch (error) {
        this.$message.error('获取店主详情失败')
      }
    },
    
    handleEdit(row) {
      this.isEdit = true
      this.dialogTitle = '编辑店主'
      this.ownerForm = { ...row }
      this.dialogVisible = true
    },
    
    async handleChangeStatus(row) {
      const newStatus = row.status === 1 ? 2 : 1
      const statusText = newStatus === 1 ? '启用' : '禁用'
      
      try {
        await this.$confirm(`确定要${statusText}该店主吗？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        await changeOwnerStatus({ id: row.id, status: newStatus })
        this.$message.success(`${statusText}成功`)
        this.fetchOwnerList()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error(`${statusText}失败`)
        }
      }
    },
    
    async handleDelete(row) {
      try {
        await this.$confirm('确定要删除该店主吗？删除后不可恢复！', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        await deleteOwner({ id: row.id })
        this.$message.success('删除成功')
        this.fetchOwnerList()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败')
        }
      }
    },

    handleChangePassword(row) {
      this.passwordForm = {
        id: row.id,
        real_name: row.real_name,
        password: '',
        confirmPassword: ''
      }
      this.passwordDialogVisible = true
      this.$nextTick(() => {
        this.$refs.passwordForm.clearValidate()
      })
    },

    async handleSubmitPassword() {
      this.$refs.passwordForm.validate(async (valid) => {
        if (valid) {
          try {
            await changeOwnerPassword({
              id: this.passwordForm.id,
              password: this.passwordForm.password
            })
            this.$message.success('密码修改成功')
            this.passwordDialogVisible = false
          } catch (error) {
            this.$message.error('密码修改失败')
          }
        }
      })
    },

    async handleSubmit() {
      this.$refs.ownerForm.validate(async (valid) => {
        if (valid) {
          try {
            if (this.isEdit) {
              await editOwner(this.ownerForm)
              this.$message.success('编辑成功')
            } else {
              await addOwner(this.ownerForm)
              this.$message.success('添加成功')
            }
            this.dialogVisible = false
            this.fetchOwnerList()
          } catch (error) {
            this.$message.error(this.isEdit ? '编辑失败' : '添加失败')
          }
        }
      })
    },
    
    viewShops(ownerId) {
      // 跳转到店铺管理页面，并筛选该店主的店铺
      this.$router.push({
        path: '/dashboard/store/shop',
        query: { owner_id: ownerId }
      })
    }
  }
}
</script>

<style scoped>
.owner-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 24px;
  color: #303133;
}

.search-form {
  background: #f5f5f5;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

.table-operations {
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: nowrap;
}

.edit-btn {
  color: #409EFF;
}

.edit-btn:hover {
  color: #66b1ff;
}

.enable-btn {
  color: #67C23A;
}

.enable-btn:hover {
  color: #85ce61;
}

.disable-btn {
  color: #E6A23C;
}

.disable-btn:hover {
  color: #ebb563;
}

.delete-btn {
  color: #F56C6C;
}

.delete-btn:hover {
  color: #f78989;
}

.password-btn {
  color: #909399;
}

.password-btn:hover {
  color: #409EFF;
}

.dialog-footer {
  text-align: right;
}

/* 头像上传样式 */
.avatar-upload-container {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.avatar-uploader {
  flex-shrink: 0;
}

.avatar-uploader >>> .el-upload {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s;
  width: 148px;
  height: 148px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-uploader >>> .el-upload:hover {
  border-color: #409EFF;
}

.avatar-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 148px;
  height: 148px;
  line-height: 148px;
  text-align: center;
}

.avatar-preview {
  width: 148px;
  height: 148px;
  display: block;
  object-fit: cover;
}

.avatar-upload-tips {
  flex: 1;
  color: #909399;
  font-size: 12px;
  line-height: 1.8;
}

.avatar-upload-tips p {
  margin: 0;
  padding: 2px 0;
}

.owner-detail {
  padding: 10px 0;
}

.detail-section {
  margin-bottom: 30px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 15px;
  padding-left: 10px;
  border-left: 4px solid #409EFF;
  line-height: 20px;
}

.detail-table {
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  overflow: hidden;
  background-color: #fff;
}

.detail-row {
  display: flex;
  border-bottom: 1px solid #EBEEF5;
  min-height: 50px;
  align-items: center;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  font-weight: 500;
  color: #606266;
  width: 150px;
  text-align: right;
  padding: 15px 20px;
  background-color: #FAFAFA;
  border-right: 1px solid #EBEEF5;
  flex-shrink: 0;
  box-sizing: border-box;
}

.detail-value {
  color: #303133;
  word-break: break-word;
  padding: 15px 20px;
  flex: 1;
  box-sizing: border-box;
}
</style>